import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sigma } from 'lucide-react';
import { weekLabel } from '../../lib/weekFilter';
import { CopyNumberButton } from '../shared/CopyNumberButton';

/** Group-sum a list by currency: sumByCurrency(rows, r => r.currency, r => r.total_net). */
export const sumByCurrency = (rows, getCur, getVal) => rows.reduce((acc, r) => {
  const v = Number(getVal(r)) || 0;
  const c = getCur(r) || 'PLN';
  acc[c] = (acc[c] || 0) + v;
  return acc;
}, {});

const money = (lang, cur, v) => {
  try {
    // Same money format as the tables (Polish grouping) so figures read alike.
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: cur, maximumFractionDigits: 2 }).format(v);
  } catch {
    return `${v.toFixed(2)} ${cur}`;
  }
};

/** Totals for whatever is currently listed: count · hours · amounts per currency. */
export const ListTotalsBar = ({ count, hours, groups = [], week, testId = 'list-totals', className = '', copyable = false }) => {
  const { t, i18n } = useTranslation();
  const lang = String(i18n.language || 'en').startsWith('pl') ? 'pl-PL' : 'en-GB';
  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 bg-slate-50/70 px-4 py-2 text-xs text-slate-600 ${className}`}
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1 font-medium text-slate-700">
        <Sigma className="h-3.5 w-3.5 text-slate-400" />
        {week ? `${weekLabel(week)}:` : t('totals.listed')}
      </span>
      <span data-testid={`${testId}-count`}>{t('totals.items', { count })}</span>
      {hours != null && (
        <span className="inline-flex items-center gap-0.5 whitespace-nowrap tabular-nums" data-testid={`${testId}-hours`}>
          {t('totals.hours', { value: new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(hours) })}
          {copyable && <CopyNumberButton value={hours} trim size="sm" testId={`${testId}-hours-copy`} />}
        </span>
      )}
      {groups.map((g) => {
        const entries = Object.entries(g.values || {});
        if (!entries.length) return null;
        return (
          <span key={g.label} className="tabular-nums" data-testid={`${testId}-${g.key || g.label}`}>
            <span className="text-slate-400">{g.label}:</span>{' '}
            <span className="font-semibold text-slate-800">
              {entries.map(([c, v], i) => (
                <React.Fragment key={c}>
                  {i > 0 && ' · '}
                  <span className="whitespace-nowrap">
                    {money(lang, c, v)}
                    {copyable && <CopyNumberButton value={v} size="sm" className="ml-0.5" testId={`${testId}-${g.key || g.label}-copy-${c}`} />}
                  </span>
                </React.Fragment>
              ))}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default ListTotalsBar;
